import Fieldset from 'components/_UI/Fieldset';
import React, { useState } from 'react'
import { Search, SlidersHorizontal } from 'lucide-react';
import styled, { css } from 'styled-components'
import colors from 'theme/colors';
import { ui } from 'theme/discover';

// O roxo da home vem da variável do tema (Home.js); fora dela, o roxo do site.
const ACCENT = `var(--home-accent, ${colors.occasionalPurple})`;

/**
 * Busca e filtros do header, no desenho do app (WUISearchBar): campo em
 * pílula cinza-claro com a lupa, e o "Filtros" em pílula contornada ao lado.
 */
export default function HeaderFilters({ onFilterClick, onSearchSubmit, activeFilterCount = 0, onInputClick }) {
    const [searchValue, setSearchValue] = useState('');

    function handleSearchClick() {
        if (onInputClick) {
            onInputClick();
            return;
        }
        if (onSearchSubmit) onSearchSubmit(searchValue);
    }

    return (
        <Container>
            <SearchField $clickable={!!onInputClick} onClick={onInputClick || undefined}>
                <SearchButton
                    type='button'
                    aria-label='Pesquisar'
                    onClick={onInputClick ? undefined : handleSearchClick}
                >
                    <Search size={16} />
                </SearchButton>
                <Fieldset
                    placeholder='Buscar eventos, lugares, artistas...'
                    onChangeValue={onInputClick ? undefined : setSearchValue}
                    readOnly={!!onInputClick}
                    onKeyUp={onInputClick ? undefined : (e) => {
                        if (e.key === 'Enter' && onSearchSubmit) {
                            onSearchSubmit(searchValue);
                        }
                    }}
                />
            </SearchField>

            <FilterButton type='button' onClick={onFilterClick} $active={activeFilterCount > 0}>
                <SlidersHorizontal size={15} />
                Filtros
                {activeFilterCount > 0 && <ActiveCount>{activeFilterCount}</ActiveCount>}
            </FilterButton>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SearchField = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 300px;
    height: 44px;
    padding: 0 16px 0 42px;
    border-radius: 999px;
    border: 1px solid ${ui.border};
    background: ${ui.surfaceMuted};
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
    ${({ $clickable }) => $clickable && css`cursor: pointer;`}

    @media screen and (min-width: 1400px) {
        width: 380px;
    }

    &:hover {
        border-color: #DCD8E8;
    }

    &:focus-within {
        background: ${colors.neutralLight};
        border-color: ${ACCENT};
        box-shadow: 0 0 0 3px color-mix(in srgb, ${ACCENT} 12%, transparent);
    }

    /* O Fieldset traz borda, fundo e respiro de formulário; aqui ele é só o texto. */
    & > div {
        width: 100%;
        padding: 0 !important;
        margin: 0 !important;

        & > input {
            width: 100%;
            padding: 0 !important;
            border: none !important;
            outline: none;
            font-size: 14px !important;
            color: ${ui.ink} !important;
            background: transparent !important;
            ${({ $clickable }) => $clickable && css`cursor: pointer;`}

            &::placeholder {
                color: ${ui.inkFaint} !important;
            }
        }
    }
`;

const SearchButton = styled.button`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    padding: 0;
    border: none;
    background: none;
    color: ${ui.inkSoft};
    cursor: pointer;
`;

const FilterButton = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 44px;
    padding: 0 16px;
    border-radius: 999px;
    border: 1px solid ${({ $active }) => $active ? ACCENT : ui.border};
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: ${({ $active }) => $active ? ACCENT : ui.ink};
    background: ${colors.neutralLight};
    cursor: pointer;
    white-space: nowrap;
    transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

    &:hover {
        border-color: ${ACCENT};
        color: ${ACCENT};
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT};
        outline-offset: 2px;
    }
`;

const ActiveCount = styled.span`
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    color: ${colors.neutralLight};
    background: ${ACCENT};
`;
